/**
 * @file api.ts
 * @brief TanStack Query hooks for the Super Admin → Analytics feature.
 *
 * @details
 * Pure consumers of pre-existing endpoints — no backend changes were
 * needed for this module. Each hook validates the response with its
 * Zod schema before handing data to React.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client / endpoints
 * - features/analytics/schemas
 *
 * @author DES
 * @date 26-05-2026
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  CompanyGrowthSchema,
  LanguageStatsSchema,
  RevenueStatsSchema,
  RevenueTrendSchema,
  SessionStatsSchema,
  SessionTrendSchema,
  SessionsPerCompanySchema,
  SessionsPerUserSchema,
  UserGrowthSchema,
  type AnalyticsRange,
  type CompanyGrowth,
  type GroupBy,
  type LanguageStatRow,
  type RevenueStats,
  type RevenueTrendPoint,
  type SessionStats,
  type SessionTrendPoint,
  type SessionsPerCompanyRow,
  type SessionsPerUserRow,
  type UserGrowth,
} from "./schemas";

// ----------------------------------------------------------------------
// Helpers — turn `{startDate,endDate,...}` objects into URLSearchParams.
// ----------------------------------------------------------------------
/**
 * The viewer's IANA zone, sent with every windowed query so the backend groups
 * day/week/month buckets on the SAME calendar the window was cut on. Without
 * it the server truncates on its own zone (UTC) and a bucket can fall outside
 * the requested range — the defect found in the invoices daily-spend feed on
 * 24-08-2026. Omitting it keeps the old UTC behaviour.
 */
function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function rangeQuery(range: AnalyticsRange & Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(range)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  usp.set("tz", viewerTimeZone());
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ----------------------------------------------------------------------
// Query keys
// ----------------------------------------------------------------------
export const analyticsKeys = {
  all: ["admin", "analytics"] as const,
  sessionStats: (range: AnalyticsRange) =>
    ["admin", "analytics", "sessions", range] as const,
  sessionTrend: (range: AnalyticsRange & { groupBy?: GroupBy }) =>
    ["admin", "analytics", "sessions", "trend", range] as const,
  revenueStats: (range: AnalyticsRange & { currency?: string }) =>
    ["admin", "analytics", "revenue", range] as const,
  revenueTrend: (range: AnalyticsRange & { groupBy?: GroupBy }) =>
    ["admin", "analytics", "revenue", "trend", range] as const,
  languageStats: (range: AnalyticsRange & { limit?: number }) =>
    ["admin", "analytics", "languages", range] as const,
  sessionsPerUser: (range: AnalyticsRange & { limit?: number }) =>
    ["admin", "analytics", "sessions", "per-user", range] as const,
  sessionsPerCompany: (range: AnalyticsRange & { limit?: number }) =>
    ["admin", "analytics", "sessions", "per-company", range] as const,
  userGrowth: (range: AnalyticsRange & { groupBy?: GroupBy }) =>
    ["admin", "analytics", "growth", "users", range] as const,
  companyGrowth: (range: AnalyticsRange & { groupBy?: GroupBy }) =>
    ["admin", "analytics", "growth", "companies", range] as const,
};

// ----------------------------------------------------------------------
// Hooks
// ----------------------------------------------------------------------
export function useSessionStats(
  range: AnalyticsRange,
  options?: Partial<UseQueryOptions<SessionStats>>,
) {
  return useQuery<SessionStats>({
    queryKey: analyticsKeys.sessionStats(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsSessions}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return SessionStatsSchema.parse(raw);
    },
    ...options,
  });
}

export function useSessionTrend(
  range: AnalyticsRange & { groupBy?: GroupBy },
  options?: Partial<UseQueryOptions<SessionTrendPoint[]>>,
) {
  return useQuery<SessionTrendPoint[]>({
    queryKey: analyticsKeys.sessionTrend(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsSessionsTrend}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return SessionTrendSchema.parse(raw);
    },
    ...options,
  });
}

export function useRevenueStats(
  range: AnalyticsRange & { currency?: string },
  options?: Partial<UseQueryOptions<RevenueStats>>,
) {
  return useQuery<RevenueStats>({
    queryKey: analyticsKeys.revenueStats(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsRevenue}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return RevenueStatsSchema.parse(raw);
    },
    ...options,
  });
}

export function useRevenueTrend(
  range: AnalyticsRange & { groupBy?: GroupBy },
  options?: Partial<UseQueryOptions<RevenueTrendPoint[]>>,
) {
  return useQuery<RevenueTrendPoint[]>({
    queryKey: analyticsKeys.revenueTrend(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsRevenueTrend}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return RevenueTrendSchema.parse(raw);
    },
    ...options,
  });
}

export function useLanguageStats(
  range: AnalyticsRange & { limit?: number },
  options?: Partial<UseQueryOptions<LanguageStatRow[]>>,
) {
  return useQuery<LanguageStatRow[]>({
    queryKey: analyticsKeys.languageStats(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsLanguages}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return LanguageStatsSchema.parse(raw);
    },
    ...options,
  });
}

export function useSessionsPerUser(
  range: AnalyticsRange & { limit?: number },
  options?: Partial<UseQueryOptions<SessionsPerUserRow[]>>,
) {
  return useQuery<SessionsPerUserRow[]>({
    queryKey: analyticsKeys.sessionsPerUser(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsSessionsPerUser}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return SessionsPerUserSchema.parse(raw);
    },
    ...options,
  });
}

export function useSessionsPerCompany(
  range: AnalyticsRange & { limit?: number },
  options?: Partial<UseQueryOptions<SessionsPerCompanyRow[]>>,
) {
  return useQuery<SessionsPerCompanyRow[]>({
    queryKey: analyticsKeys.sessionsPerCompany(range),
    queryFn: async () => {
      const url = `${adminEndpoints.analyticsSessionsPerCompany}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return SessionsPerCompanySchema.parse(raw);
    },
    ...options,
  });
}

export function useUserGrowth(
  range: AnalyticsRange & { groupBy?: GroupBy },
  options?: Partial<UseQueryOptions<UserGrowth>>,
) {
  return useQuery<UserGrowth>({
    queryKey: analyticsKeys.userGrowth(range),
    queryFn: async () => {
      const url = `${adminEndpoints.growthUsers}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return UserGrowthSchema.parse(raw);
    },
    ...options,
  });
}

export function useCompanyGrowth(
  range: AnalyticsRange & { groupBy?: GroupBy },
  options?: Partial<UseQueryOptions<CompanyGrowth>>,
) {
  return useQuery<CompanyGrowth>({
    queryKey: analyticsKeys.companyGrowth(range),
    queryFn: async () => {
      const url = `${adminEndpoints.growthCompanies}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return CompanyGrowthSchema.parse(raw);
    },
    ...options,
  });
}
